// Role object structure from backend
export interface UserRole {
  id: number;
  name: string;
  guard_name?: string;
  created_at?: string | null;
  updated_at?: string | null;
}

/** Membership plan returned by the backend API */
export interface MembershipPlan {
  id: number;
  name: string;
  description: string | null;
  price: string;
  currency: string;
  duration_type: string;
  duration_value: number;
  status: string;
  free_access: boolean;
  max_active_listings: number;
  max_draft_listings: number;
  messages_allowed: number;
  nextcloud_integration: boolean;
  nextcloud_disc: string | null;
  discourse_integration: boolean;
  synch_to_stripe: boolean;
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Frozen snapshot of a package at order creation time */
export interface OrderPackageSnapshot {
  id: number;
  name: string;
  price: string;
  currency: string;
  duration: string | null;
  duration_type: string;
  duration_value: number;
  max_active_listings: number;
  max_draft_listings: number;
  nextcloud_integration: boolean;
  nextcloud_disc: string | null;
  discourse_integration: boolean;
  stripe_price_id: string | null;
  stripe_product_id: string | null;
  messages_allowed: number;
  free_access: boolean;
}

/** Order object from the backend API */
export interface Order {
  id: string;
  user_id: string;
  code: string;
  package_id: number;
  package_snapshot: OrderPackageSnapshot;
  subscription_id: string | null;
  checkout_session_id: string | null;
  type: string;
  total_price: string;
  currency: string;
  status: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  package: MembershipPlan | null;
  subscription: unknown | null;
}

// User object structure
export interface User {
  id: string;
  email: string;
  name?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  avatar?: string | null;
  bio?: string | null;
  roles?: UserRole[];
  created_at?: string;
  updated_at?: string;
  membership?: MembershipPlan | null;
  orders?: Order[];
}

/**
 * Checks if the given user has admin privileges.
 * Looks for a role with name 'admin' in the user's roles array.
 * 
 * **Important**: This is a client-side check for UI purposes only.
 * Backend API endpoints must also enforce admin authorization server-side.
 * 
 * @param user - The user object to check, or null if not authenticated
 * @returns true if the user has the 'admin' role, false otherwise
 * 
 * @example
 * // Conditionally render admin-only UI elements
 * const { user } = useAuthStore()
 * {isAdmin(user) && <Link href="/studio">Studio</Link>}
 * 
 * @example
 * // Guard a page or component
 * if (!isAdmin(user)) {
 *   router.push('/')
 *   return null
 * }
 */
export const isAdmin = (user: User | null): boolean => {
  if (!user?.roles || !Array.isArray(user.roles)) {
    return false;
  }
  return user.roles.some(role => role.name === 'admin');
};

// Request payload sent to backend for login
export interface LoginData {
  email: string;
  password: string;
  cf_turnstile_response?: string;
}

// Response from backend on successful login
export interface LoginResponse {
  token: string;   // JWT token
  user?: User;     // Optional user object
}

// Request payload for registration
export interface RegisterData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  password_confirmation: string;
  cf_turnstile_response?: string;
  custom_fields?: Record<string, string>;
}

// Request payload for Fanbasis token-based registration
export interface FanbasisRegisterData {
  token: string;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  password: string;
  password_confirmation: string;
  custom_fields?: Record<string, string>;
}

// Response from backend on successful registration
export interface RegisterResponse {
  token?: string;
  user?: User;
  message?: string;
}

// Request payload for forgot password
export interface ForgotPasswordData {
  email: string;
  cf_turnstile_response?: string;
}

// Request payload for password reset
export interface ResetPasswordData {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
}

// Request payload for changing user password (authenticated)
export interface ChangePasswordData {
  email: string;
  old_password: string;
  new_password: string;
  new_password_confirmation: string;
}

// Generic API response wrapper
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;  // Field-level validation errors
  status?: number;  // HTTP status code
}
